import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FaEdit, 
  FaFile, 
  FaSpinner, 
  FaArrowLeft,
  FaTrash,
  FaKey,
  FaSave
} from 'react-icons/fa';
import axios from 'axios';
import config from '../config/api';

const EditHomework = () => {
  const { homeworkId } = useParams();
  const navigate = useNavigate();
  
  const [homework, setHomework] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subject, setSubject] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [file, setFile] = useState(null);
  const [answerKey, setAnswerKey] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [removeHomeworkFile, setRemoveHomeworkFile] = useState(false);
  const [removeAnswerKeyFile, setRemoveAnswerKeyFile] = useState(false);
  const [showAnswerKeyToStudent, setShowAnswerKeyToStudent] = useState(false);
  const [requiresSubmission, setRequiresSubmission] = useState(true);

  const allowedFileTypes = ['pdf', 'doc', 'docx', 'txt', 'png', 'jpg', 'jpeg'];

  useEffect(() => {
    const fetchHomework = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${config.apiURL}/teacher/homework/${homeworkId}/edit`);
        const homeworkData = response.data.homework;
        
        setHomework(homeworkData);
        setTitle(homeworkData.title);
        setDescription(homeworkData.description);
        setSubject(homeworkData.subject);
        setDueDate(new Date(homeworkData.due_date).toISOString().slice(0, 16));
        setShowAnswerKeyToStudent(homeworkData.show_answer_key_to_student);
        setRequiresSubmission(homeworkData.requires_submission);
        setAllStudents(homeworkData.all_students || []);
        setSelectedStudents((homeworkData.students || []).map(s => s.id));
      } catch (err) {
        console.error('Erro ao carregar lição:', err);
        setError('Erro ao carregar dados da lição');
      } finally {
        setLoading(false);
      }
    };

    fetchHomework();
  }, [homeworkId]);

  const handleFileChange = (e, setFileFunction) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.name.split('.').pop().toLowerCase();
      if (allowedFileTypes.includes(fileType)) {
        setFileFunction(selectedFile);
        setError('');
      } else {
        setError('Tipo de arquivo não permitido. Use: PDF, DOC, DOCX, TXT, PNG, JPG, JPEG');
      }
    }
  };

  const handleStudentChange = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const selectAllStudents = () => {
    setSelectedStudents(allStudents.map(student => student.id));
  };

  const clearAllStudents = () => {
    setSelectedStudents([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !description || !subject || !dueDate) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (selectedStudents.length === 0) {
      setError('Por favor, selecione pelo menos um estudante.');
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('subject', subject);
    formData.append('due_date', dueDate);
    formData.append('show_answer_key_to_student', showAnswerKeyToStudent);
    formData.append('requires_submission', requiresSubmission);
    formData.append('student_ids', JSON.stringify(selectedStudents));
    
    if (file) {
      formData.append('homework_file', file);
    }
    
    if (answerKey) {
      formData.append('answer_key', answerKey);
    }

    if (removeHomeworkFile) {
      formData.append('remove_homework_file', 'true');
    }

    if (removeAnswerKeyFile) {
      formData.append('remove_answer_key_file', 'true');
    }

    try {
      setSaving(true);
      const response = await axios.put(`${config.apiURL}/teacher/homework/${homeworkId}/update`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.message) {
        navigate('/homeworks');
      } else {
        setError(response.data.message || 'Erro ao atualizar lição');
      }
    } catch (err) {
      console.error('Erro ao atualizar lição:', err);
      setError('Erro ao atualizar lição. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <FaSpinner className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Carregando dados da lição...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !homework) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button 
              onClick={() => navigate('/homeworks')}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Voltar para Lições
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/homeworks')}
            className="mb-4 flex items-center space-x-2 text-blue-600 hover:text-blue-800"
          >
            <FaArrowLeft className="w-4 h-4" />
            <span>Voltar para Lições</span>
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
            <FaEdit className="text-orange-600" />
            <span>Editar Lição de Casa</span>
          </h1>
          <p className="mt-2 text-gray-600">
            Modifique os detalhes, arquivos e alunos da lição
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informações Básicas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título da Lição *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite o título da lição"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Matéria *
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Matemática, Português, etc."
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Descreva a lição e instruções para os alunos"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data de Vencimento *
              </label>
              <input
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Arquivos */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Arquivos</h3>
              
              {/* Arquivo da Lição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arquivo da Lição
                </label>
                
                {homework?.homework_file && !removeHomeworkFile && !file && (
                  <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FaFile className="text-blue-600" />
                      <span className="text-sm text-blue-700">Arquivo atual da lição</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => window.open(homework.homework_file, '_blank')}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Ver Arquivo
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemoveHomeworkFile(true)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {(removeHomeworkFile || !homework?.homework_file || file) && (
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer bg-blue-50 border border-blue-200 rounded-md px-4 py-2 hover:bg-blue-100 transition-colors">
                      <FaFile className="text-blue-600" />
                      <span className="text-blue-600 text-sm">
                        {file ? file.name : 'Selecionar novo arquivo'}
                      </span>
                      <input
                        type="file"
                        onChange={(e) => handleFileChange(e, setFile)}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                      />
                    </label>
                    {removeHomeworkFile && (
                      <button
                        type="button"
                        onClick={() => setRemoveHomeworkFile(false)}
                        className="text-gray-600 text-sm"
                      >
                        Cancelar remoção
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Arquivo do Gabarito */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gabarito (Opcional)
                </label>
                
                {homework?.answer_key_file && !removeAnswerKeyFile && !answerKey && (
                  <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FaKey className="text-green-600" />
                      <span className="text-sm text-green-700">Gabarito atual</span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => window.open(homework.answer_key_file, '_blank')}
                        className="text-green-600 hover:text-green-800 text-sm"
                      >
                        Ver Gabarito
                      </button>
                      <button
                        type="button"
                        onClick={() => setRemoveAnswerKeyFile(true)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {(removeAnswerKeyFile || !homework?.answer_key_file || answerKey) && (
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer bg-green-50 border border-green-200 rounded-md px-4 py-2 hover:bg-green-100 transition-colors">
                      <FaKey className="text-green-600" />
                      <span className="text-green-600 text-sm">
                        {answerKey ? answerKey.name : 'Selecionar novo gabarito'}
                      </span>
                      <input
                        type="file"
                        onChange={(e) => handleFileChange(e, setAnswerKey)}
                        className="hidden"
                        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
                      />
                    </label>
                    {removeAnswerKeyFile && (
                      <button
                        type="button"
                        onClick={() => setRemoveAnswerKeyFile(false)}
                        className="text-gray-600 text-sm"
                      >
                        Cancelar remoção
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Configurações */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Configurações</h3>
              
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={requiresSubmission}
                    onChange={(e) => setRequiresSubmission(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Requer entrega de arquivo pelos alunos</span>
                </label>

                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={showAnswerKeyToStudent}
                    onChange={(e) => setShowAnswerKeyToStudent(e.target.checked)}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Mostrar gabarito para os alunos</span>
                </label>
              </div>
            </div>

            {/* Seleção de Alunos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Selecione os Alunos *
              </label>
              
              <div className="mb-4 flex space-x-2">
                <button
                  type="button"
                  onClick={selectAllStudents}
                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                >
                  Selecionar Todos
                </button>
                <button
                  type="button"
                  onClick={clearAllStudents}
                  className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                >
                  Limpar Seleção
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
                {allStudents.map((student) => (
                  <label key={student.id} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStudents.includes(student.id)}
                      onChange={() => handleStudentChange(student.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{student.name} - {student.grade}</span>
                  </label>
                ))}
              </div>
              
              <p className="text-sm text-gray-500 mt-2">
                {selectedStudents.length} de {allStudents.length} aluno(s) selecionado(s)
              </p>
            </div>

            {/* Botões de Ação */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/homeworks')}
                className="px-6 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {saving ? (
                  <>
                    <FaSpinner className="animate-spin w-4 h-4" />
                    <span>Salvando...</span>
                  </>
                ) : (
                  <>
                    <FaSave className="w-4 h-4" />
                    <span>Salvar Alterações</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditHomework; 